/**
 * バナーレイアウトのキャンバス (docs/features/banner-layout-template-plan.md §7)。
 *
 * BE のガイド PNG (backend/services/banner_layout/guide.py) と同じ配色を DOM で描く: グレーの背景に、白塗り + 役割色の枠 + 左上の役割名。
 * interactive のときは自前の pointer events で移動・8ハンドルのリサイズ・スナップ (ガイド線) を行う。
 * 背景の分割線もキャンバス上で操作する (§20): 線を掴んで平行移動 (位置)・両端のつまみで回転 (傾き)。中心 / 0度に吸着。
 * 分割があるときは写真側・文字側の重心に「写真」「文字」の大きな目印を出す (「左」「右」より直感的・利用者指示 2026-10-19)。
 * 選択中の要素の上には回転つまみを出し、掴んで回せる (§21: 0度に吸着。数値はフローティング操作列のポップオーバーで)。
 * interactive=false はミニプレビュー (入口・プリセット / テンプレートのカード) に使う。
 */
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { BANNER_LAYOUT_ROLES, elementLabels } from '../../../features/bannerLayout/roles';
import {
  RESIZE_HANDLES,
  dragElementRotation,
  dragSplitAngle,
  withRotation,
  dragSplitPosition,
  moveRect,
  resizeRect,
  snapMove,
  snapResize,
  splitLine,
  splitSideCentroids,
  splitVisualPolygon,
  type ResizeHandle,
  type SnapGuides,
} from '../../../features/bannerLayout/geometry';
import { resolveProductImage, type LayoutImage } from '../../../features/bannerLayout/productImages';
import { parseRatio, type BannerLayoutBackground, type BannerLayoutElement } from '../../../features/bannerLayout/types';
import styles from './BannerLayout.module.css';

type Props = {
  aspectRatio: string;
  elements: BannerLayoutElement[];
  /** 選択中の画像 (選択順)。商品枠は「何枚目か」でここから解決して contain で表示する (§17)。無い枠は役割色の枠と「商品」だけ */
  productImages?: LayoutImage[];
  /** 要素 id → 枠に入る文言 (「テキスト」欄の値・textSync.assignedLayoutTexts)。1行目を枠に表示する */
  texts?: Record<string, string>;
  /** 背景の分割 (§16)。写真側を濃いグレーで塗る (ガイド PNG と同じ) */
  background?: BannerLayoutBackground;
  /** 長辺の px */
  size: number;
  interactive?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  onChange?: (elements: BannerLayoutElement[]) => void;
  /** 背景の分割線をキャンバス上で動かしたとき (§20)。無ければ分割線は操作できない */
  onBackgroundChange?: (background: BannerLayoutBackground) => void;
  /** ドラッグ (移動・リサイズ・分割線) の開始 / 終了。親が「元に戻す」の履歴をドラッグ1回単位で積むために使う */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDelete?: (id: string) => void;
};

type DragState =
  | {
      kind: 'element';
      id: string;
      mode: 'move' | ResizeHandle | 'rotate';
      startX: number;
      startY: number;
      origin: BannerLayoutElement;
    }
  | {
      /** 背景の分割線 (§20): position = 線を掴んで平行移動、rotate = 端 (end) を掴んで回す */
      kind: 'split';
      mode: 'position' | 'rotate';
      end: 0 | 1;
      startX: number;
      startY: number;
      origin: BannerLayoutBackground;
    };

export function canvasDims(aspectRatio: string, size: number): { width: number; height: number } {
  const r = parseRatio(aspectRatio) ?? 1;
  return r >= 1 ? { width: size, height: Math.round(size / r) } : { width: Math.round(size * r), height: size };
}

/**
 * 枠 (stage) に収まる canvasDims の size (長辺)。上限 max・下限 min。枠の大きさが分からないときは max。
 * エディタは画面の高さが足りないと固定 560px のキャンバスがカード列やフッターに重なるため、空き領域に合わせて縮める。
 */
export function fitCanvasSize(
  aspectRatio: string,
  stage: { width: number; height: number } | null,
  max = 560,
  min = 160,
): number {
  if (!stage || stage.width <= 0 || stage.height <= 0) return max;
  const r = parseRatio(aspectRatio) ?? 1;
  const fit = r >= 1 ? Math.min(stage.width, stage.height * r) : Math.min(stage.height, stage.width / r);
  return Math.max(min, Math.min(max, Math.floor(fit)));
}

const pct = (v: number) => `${v * 100}%`;

export function BannerLayoutCanvas({
  aspectRatio,
  elements,
  productImages,
  texts,
  background,
  size,
  interactive = false,
  selectedId = null,
  onSelect,
  onChange,
  onBackgroundChange,
  onDragStart,
  onDragEnd,
  onDelete,
}: Props) {
  const { width, height } = canvasDims(aspectRatio, size);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [guides, setGuides] = useState<SnapGuides>({ x: [], y: [] });
  // 分割線をドラッグ中 (線を太く見せる・カーソルを掴んだ形にする)
  const [splitDragging, setSplitDragging] = useState<'position' | 'rotate' | null>(null);
  const labels = elementLabels(elements);
  // 枠は塗らず線だけで描く (ガイド PNG と同じ)。重なりは意図した構図として許し、警告しない。
  // 前後関係は AI に伝わらないので持たない。クリックで選べるよう、大きい枠ほど下に重ねる (選択中は最前面)
  const stackOrder = useMemo(() => {
    const byArea = elements.map((el, i) => ({ i, area: el.w * el.h })).sort((a, b) => b.area - a.area);
    const z = new Array<number>(elements.length);
    byArea.forEach(({ i }, rank) => {
      z[i] = rank + 1;
    });
    return z;
  }, [elements]);
  const small = !interactive;

  const replace = (next: BannerLayoutElement) => onChange?.(elements.map((e) => (e.id === next.id ? next : e)));

  const startDrag = (e: ReactPointerEvent, el: BannerLayoutElement, mode: 'move' | ResizeHandle | 'rotate') => {
    if (!interactive || e.button > 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.(el.id);
    frameRef.current?.focus();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: 'element', id: el.id, mode, startX: e.clientX, startY: e.clientY, origin: el };
    onDragStart?.();
  };

  /** 分割線のドラッグ開始 (§20)。要素の選択は外す */
  const startSplitDrag = (e: ReactPointerEvent, mode: 'position' | 'rotate', end: 0 | 1 = 1) => {
    if (!interactive || !background || !onBackgroundChange || e.button > 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.(null);
    frameRef.current?.focus();
    (e.currentTarget as Element as HTMLElement).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: 'split', mode, end, startX: e.clientX, startY: e.clientY, origin: background };
    setSplitDragging(mode);
    onDragStart?.();
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = (e.clientX - drag.startX) / width;
    const dy = (e.clientY - drag.startY) / height;
    // Option (Alt) / Cmd (macOS) / Ctrl (Windows) を押している間はスナップしない
    const tol = e.altKey || e.metaKey || e.ctrlKey ? 0 : undefined;
    if (drag.kind === 'split') {
      if (drag.mode === 'position') {
        const { background: next, snapped } = dragSplitPosition(drag.origin, dx, dy, tol !== 0);
        // 中心に吸着したときは要素のスナップと同じガイド線を出す
        setGuides(snapped ? (next.orientation === 'vertical' ? { x: [0.5], y: [] } : { x: [], y: [0.5] }) : { x: [], y: [] });
        onBackgroundChange?.(next);
      } else {
        const rect = frameRef.current?.getBoundingClientRect();
        const pointer: [number, number] = rect ? [e.clientX - rect.left, e.clientY - rect.top] : [e.clientX, e.clientY];
        onBackgroundChange?.(dragSplitAngle(drag.origin, pointer, width, height, drag.end, tol !== 0).background);
      }
      return;
    }
    if (drag.mode === 'rotate') {
      // 回転つまみ (§21): 掴んだ位置からポインタまで、要素の中心の周りに回った角度を元の傾きに足す。
      // 中心は回転前の矩形の中心 (回転しても動かない)。クライアント座標のまま比べる (中心もクライアント座標にする)
      const rect = frameRef.current?.getBoundingClientRect();
      const ox = rect?.left ?? 0;
      const oy = rect?.top ?? 0;
      const center: [number, number] = [ox + (drag.origin.x + drag.origin.w / 2) * width, oy + (drag.origin.y + drag.origin.h / 2) * height];
      const { rotation } = dragElementRotation(center, [drag.startX, drag.startY], [e.clientX, e.clientY], drag.origin.rotation ?? 0, tol !== 0);
      replace(withRotation(drag.origin, rotation));
      return;
    }
    const others = elements.filter((o) => o.id !== drag.id);
    if (drag.mode === 'move') {
      const moved = moveRect(drag.origin, dx, dy);
      const snapped = tol === 0 ? { rect: moved, guides: { x: [], y: [] } } : snapMove(moved, others);
      setGuides(snapped.guides);
      replace(snapped.rect);
    } else {
      const resized = resizeRect(drag.origin, drag.mode, dx, dy);
      const snapped = tol === 0 ? { rect: resized, guides: { x: [], y: [] } } : snapResize(resized, drag.mode, others);
      setGuides(snapped.guides);
      replace(snapped.rect);
    }
  };

  const endDrag = () => {
    const wasDragging = !!dragRef.current;
    dragRef.current = null;
    setGuides({ x: [], y: [] });
    setSplitDragging(null);
    if (wasDragging) onDragEnd?.();
  };
  const splitEditable = interactive && !!background && !!onBackgroundChange;
  // 「写真」「文字」の目印の文字の大きさ: 短辺の 11% (18〜48px)
  const sideLabelSize = Math.max(18, Math.min(48, Math.round(Math.min(width, height) * 0.11)));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || !selectedId) return;
    const el = elements.find((x) => x.id === selectedId);
    if (!el) return;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      onDelete?.(el.id);
      return;
    }
    const step = e.shiftKey ? 0.05 : 0.01;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = delta[e.key];
    if (!d) return;
    e.preventDefault();
    replace(moveRect(el, d[0], d[1]));
  };

  return (
    <div
      ref={frameRef}
      className={`${styles.canvas} ${small ? styles.canvasSmall : ''}`}
      style={{ width, height }}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={onKeyDown}
      onPointerMove={interactive ? onPointerMove : undefined}
      onPointerUp={interactive ? endDrag : undefined}
      onPointerCancel={interactive ? endDrag : undefined}
      onPointerDown={interactive ? () => onSelect?.(null) : undefined}
      data-testid={interactive ? 'banner-layout-canvas' : undefined}
      aria-label={interactive ? 'レイアウトのキャンバス' : undefined}
    >
      {background && (
        <svg className={styles.splitBackground} width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" data-testid="banner-layout-split">
          <polygon points={splitVisualPolygon(background, width, height).map(([x, y]) => `${x},${y}`).join(' ')} />
        </svg>
      )}
      {background && !small && (() => {
        // 写真側・文字側の目印 (§20): 「左」「右」の切替より何がどこに載るかが直感的に分かるよう、領域の重心に大きな太字で出す
        const { visual, text } = splitSideCentroids(background, width, height);
        return (
          <>
            <span className={`${styles.sideLabel} ${styles.sideLabelVisual}`} style={{ left: visual[0], top: visual[1], fontSize: sideLabelSize }} data-testid="banner-layout-side-visual">写真</span>
            <span className={`${styles.sideLabel} ${styles.sideLabelText}`} style={{ left: text[0], top: text[1], fontSize: sideLabelSize }} data-testid="banner-layout-side-text">文字</span>
          </>
        );
      })()}
      {elements.map((el, i) => {
        const role = BANNER_LAYOUT_ROLES[el.kind];
        const selected = interactive && el.id === selectedId;
        const thumb = el.kind === 'product' ? resolveProductImage(el, productImages ?? [])?.thumbUrl : undefined;
        const firstLine = (texts?.[el.id] ?? '').split('\n')[0];
        return (
          <div
            key={el.id}
            className={`${styles.element} ${selected ? styles.elementSelected : ''}`}
            style={{
              left: pct(el.x),
              top: pct(el.y),
              width: pct(el.w),
              height: pct(el.h),
              borderColor: role.color,
              borderStyle: el.kind === 'logo' ? 'dashed' : 'solid',
              zIndex: selected ? elements.length + 1 : stackOrder[i],
              // 傾き (§16): 中心を軸に回す。移動・リサイズは回転前の矩形に対して行う
              ...(el.rotation ? { transform: `rotate(${el.rotation}deg)` } : {}),
            }}
            onPointerDown={(e) => startDrag(e, el, 'move')}
            data-testid={interactive ? `banner-layout-element-${el.kind}` : undefined}
          >
            {thumb && <img src={thumb} alt="" className={styles.elementImage} draggable={false} />}
            <span className={styles.elementLabel} style={{ color: role.color }}>
              {labels[i]}
            </span>
            {!small && firstLine && <span className={styles.elementText}>{firstLine}</span>}
            {selected &&
              RESIZE_HANDLES.map((h) => (
                <span
                  key={h}
                  className={`${styles.handle} ${styles[`handle_${h}`]}`}
                  onPointerDown={(e) => startDrag(e, el, h)}
                  data-testid={`banner-layout-handle-${h}`}
                />
              ))}
            {selected && (
              // 回転つまみ (§21): 下辺の中央から少し下に出す (上は操作列)。要素ごと回るので、回した後も下辺の中央に付いてくる
              <span className={styles.rotateStem} aria-hidden="true">
                <span className={styles.rotateHandle} onPointerDown={(e) => startDrag(e, el, 'rotate')} data-testid="banner-layout-rotate-handle" title="ドラッグで回転 (0度に吸着・Option / Cmd / Ctrl で解除)" />
              </span>
            )}
          </div>
        );
      })}
      {background && (() => {
        // 分割線は要素の上 (最前面) に破線で描く。商品枠が写真側を覆っても線の位置が見える (ガイド PNG と同じ・§16)
        const pts = splitLine(background, width, height);
        if (pts.length !== 2) return null;
        const [[x1, y1], [x2, y2]] = pts;
        const moveCursor = background.orientation === 'vertical' ? 'ew-resize' : 'ns-resize';
        return (
          <svg
            className={`${styles.splitLine} ${splitEditable ? styles.splitLineEditable : ''} ${splitDragging ? styles.splitLineDragging : ''}`}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            data-testid="banner-layout-split-line"
          >
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fff" strokeWidth={small ? 1.5 : 3} />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#000" strokeWidth={small ? 0.75 : 1.5} strokeDasharray={small ? '2 2' : '6 6'} />
            {splitEditable && (
              <>
                {/* 線の上の透明な太い当たり判定: 掴んで平行移動 (位置)。ホバーで線が太くなる */}
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  className={styles.splitHit}
                  style={{ cursor: splitDragging === 'position' ? 'grabbing' : moveCursor }}
                  onPointerDown={(e) => startSplitDrag(e, 'position')}
                  data-testid="banner-layout-split-grip"
                />
                {/* 両端のつまみ: 掴んで回す (傾き) */}
                {pts.map(([x, y], i) => (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={7}
                    className={styles.splitEnd}
                    style={{ cursor: splitDragging === 'rotate' ? 'grabbing' : 'grab' }}
                    onPointerDown={(e) => startSplitDrag(e, 'rotate', i as 0 | 1)}
                    data-testid={`banner-layout-split-end-${i}`}
                  />
                ))}
              </>
            )}
          </svg>
        );
      })()}
      {guides.x.map((x) => (
        <span key={`gx${x}`} className={styles.guideLineV} style={{ left: pct(x) }} />
      ))}
      {guides.y.map((y) => (
        <span key={`gy${y}`} className={styles.guideLineH} style={{ top: pct(y) }} />
      ))}
    </div>
  );
}
